import { commonIcons } from '@apps-common/config/icons'
import classNames from 'classnames'
import moment from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'
import { Tooltip } from 'react-tooltip'

interface IProps {
  id: string
  timestamp?: string
  frequency?: number
  interval?: number
  className?: string
  normalClassName?: string
  warningClassName?: string
  dangerClassName?: string
  warningAfterSeconds?: number
  dangerAfterSeconds?: number
}

export const SecondsSinceTick: React.FC<IProps> = ({
  id,
  timestamp = moment().toISOString(),
  frequency = 90,
  interval = 1000,
  className,
  normalClassName,
  warningClassName,
  dangerClassName,
  warningAfterSeconds,
  dangerAfterSeconds
}) => {
  const baseMoment = useMemo(() => {
    try {
      const parsed = moment(timestamp)
      return parsed.isValid() ? parsed : moment()
    } catch {
      return moment()
    }
  }, [timestamp])

  const [elapsedSeconds, setElapsedSeconds] = useState(() => Math.max(0, moment().diff(baseMoment, 'seconds')))

  useEffect(() => {
    setElapsedSeconds(Math.max(0, moment().diff(baseMoment, 'seconds')))

    const timer = setInterval(() => {
      setElapsedSeconds(Math.max(0, moment().diff(baseMoment, 'seconds')))
    }, interval)

    return () => clearInterval(timer)
  }, [baseMoment, interval])

  const formatElapsed = (totalSeconds: number) => {
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (days > 0) {
      return `${days}d ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
    if (minutes > 0) {
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    return `${seconds}s`
  }

  const warnAfter = warningAfterSeconds ?? frequency
  const dangerAfter = dangerAfterSeconds ?? warnAfter * 2

  const stateClassName = elapsedSeconds >= dangerAfter ? dangerClassName : elapsedSeconds >= warnAfter ? warningClassName : normalClassName

  return (
    <div data-tooltip-id={id} data-tooltip-content='Time since last poll' className='flex'>
      <div
        className={classNames('flex items-center justify-between text-[0.8rem]', className, stateClassName)}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        <commonIcons.mmEventTimerTest />
        <span className='inline-block text-right' style={{ minWidth: '8ch' }}>
          {formatElapsed(elapsedSeconds)}
        </span>
      </div>
      <Tooltip id={id} place='top' className='hidden sm:inline-block !bg-surface !text-text !drop-shadow-sm z-[60]' />
    </div>
  )
}
